import { Card, CardBody, CardHeader, Heading, Text } from '@chakra-ui/react'
import { DDSTheme, ddsTokens } from '@ds/index'
import { parseISO } from 'date-fns'
import { useMemo } from 'react'
import {
	Bar,
	BarChart,
	CartesianGrid,
	Legend,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from 'recharts'

import { Granularity, MeetingData } from '../types'
import { formatPeriodLabelForChart } from '../utils'
import ChartSpinner from './ChartSpinner'
import ChartTooltip from './ChartTooltip'
import LegendContent from './LegendContent'

// Custom rounded top bar shape
const RoundedTopBar = (props: any) => {
	const { x, y, width, height, fill } = props
	const radius = 4

	// If height is very small (zero value), show a thin rectangle
	if (height < 2) {
		const lineHeight = 2
		return (
			<rect
				x={x}
				y={y - lineHeight / 2}
				width={width}
				height={lineHeight}
				fill={fill}
			/>
		)
	}

	// Normal bar with rounded top
	return (
		<path
			d={`
				M${x},${y + height}
				L${x},${y + radius}
				Q${x},${y} ${x + radius},${y}
				L${x + width - radius},${y}
				Q${x + width},${y} ${x + width},${y + radius}
				L${x + width},${y + height}
				L${x},${y + height}
				Z
			`}
			fill={fill}
		/>
	)
}

interface MeetingsScheduledProps {
	meetings: MeetingData[] | undefined
	granularity: Granularity
	isLoading?: boolean
}

const MeetingsScheduled = ({
	meetings,
	granularity,
	isLoading = false,
}: MeetingsScheduledProps) => {
	const tokens = ddsTokens[DDSTheme.light]

	const processedData = useMemo(() => {
		if (!meetings || meetings.length === 0) {
			return []
		}

		// Sort by time_group if needed
		const sortedMeetings = [...meetings].sort(
			(a, b) =>
				parseISO(a.time_group).getTime() - parseISO(b.time_group).getTime(),
		)

		return sortedMeetings.map((meeting) => {
			const periodLabel = formatPeriodLabelForChart(
				meeting.time_group,
				granularity,
			)

			return {
				timeLabel: periodLabel,
				booked: meeting.meetings_created_count || 0,
				rescheduled: meeting.meetings_rescheduled_count || 0,
				cancelled: meeting.meetings_cancelled_count || 0,
			}
		})
	}, [meetings, granularity])

	const createdColor = tokens.primitive.light.primary[400]
	const rescheduledColor = tokens.primitive.light.warning[400]
	const cancelledColor = tokens.primitive.light.danger[400]

	return (
		<Card
			shadow="sm"
			p={4}
			border={`1px solid ${tokens.primitive.light.gray[100]}`}
		>
			<CardHeader p={0} mb={2}>
				<Heading size="sm">Meetings Activity</Heading>
				<Text fontSize="sm" color="gray.500">
					Number of meetings booked, rescheduled, and cancelled over time
				</Text>
			</CardHeader>
			<CardBody p={0} position="relative" flexGrow={1}>
				{isLoading && <ChartSpinner />}
				<ResponsiveContainer width="100%" height={250}>
					<BarChart
						data={processedData}
						margin={{ top: 24, right: 20, bottom: 0 }}
						barGap={0}
						barCategoryGap="20%"
					>
						<CartesianGrid
							horizontal
							vertical={false}
							stroke="#E2E2E4"
							strokeDasharray="4 2"
						/>
						<Legend content={<LegendContent />} />
						<XAxis dataKey="timeLabel" tick={{ fill: '#666666' }} dy={5} />
						<YAxis tickLine={false} tick={{ fill: '#666666' }} />
						<Tooltip
							cursor={{
								fill: '#ECECED',
							}}
							contentStyle={{
								backgroundColor: tokens.primitive.light.gray[900],
								borderColor: 'transparent',
								color: tokens.primitive.light.white[100],
								borderRadius: '4px',
								padding: '8px 12px',
							}}
							content={<ChartTooltip />}
						/>

						<Bar
							dataKey="booked"
							stackId="a"
							fill={createdColor}
							name="Booked"
							barSize={140}
						/>
						<Bar
							dataKey="rescheduled"
							stackId="a"
							fill={rescheduledColor}
							name="Rescheduled"
							barSize={140}
						/>
						<Bar
							dataKey="cancelled"
							stackId="a"
							fill={cancelledColor}
							name="Cancelled"
							shape={<RoundedTopBar />}
							barSize={140}
						/>
					</BarChart>
				</ResponsiveContainer>
			</CardBody>
		</Card>
	)
}

export default MeetingsScheduled
